import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "../lib/api/apiService";
import { getCartQueryKey, useCartQuery } from "../hooks/useCartQuery";
import {
  getCartItemsArrayFromCartApiResponse,
  normalizeServerItemToCartItem,
} from "../utils/productHelpers";
import useCurrentUser from "../hooks/useCurrentUser";
import { CART_STORAGE_KEY, clearLocalCartStorage } from "../utils/cartStorage";

const CartContext = createContext();

const loadPersistedCart = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item } = action.payload;
      const index = state.items.findIndex(
        (cartItem) =>
          cartItem.productId === item.productId &&
          cartItem.variantId === item.variantId &&
          cartItem.optionKey === item.optionKey
      );

      let nextItems;

      if (index >= 0) {
        nextItems = [...state.items];
        nextItems[index] = {
          ...nextItems[index],
          quantity: nextItems[index].quantity + item.quantity,
        };
      } else {
        nextItems = [...state.items, item];
      }

      return { ...state, items: nextItems };
    }
    case "UPDATE_QUANTITY": {
      const { productId, variantId, optionKey, quantity } = action.payload;
      const nextItems = state.items
        .map((item) => {
          if (
            item.productId === productId &&
            item.variantId === variantId &&
            item.optionKey === optionKey
          ) {
            return { ...item, quantity };
          }
          return item;
        })
        .filter((item) => item.quantity > 0);

      return { ...state, items: nextItems };
    }
    case "REMOVE_ITEM": {
      const { productId, variantId, optionKey } = action.payload;
      const nextItems = state.items.filter(
        (item) =>
          !(
            item.productId === productId &&
            item.variantId === variantId &&
            item.optionKey === optionKey
          )
      );
      return { ...state, items: nextItems };
    }
    case "MERGE_ITEM_DETAILS": {
      const { productId, variantId, optionKey, updates } = action.payload;
      const nextItems = state.items.map((item) => {
        if (
          item.productId === productId &&
          item.variantId === variantId &&
          item.optionKey === optionKey
        ) {
          return { ...item, ...updates };
        }
        return item;
      });
      return { ...state, items: nextItems };
    }
    case "CLEAR":
      // Vider le panier (ne pas utiliser initialState qui recharge localStorage — sinon useCartSync
      // « vide » puis réinjecte le serveur et ADD_ITEM fusionne avec l’ancien état → quantités doublées)
      return { ...state, items: [] };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, undefined, () => ({
    items: loadPersistedCart(),
  }));

  // Persister le panier en localStorage (invités). Au logout, le panier local est vidé.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
    } catch {
      // Ignorer les erreurs (quota, etc.)
    }
  }, [state.items]);

  const totals = useMemo(() => {
    const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = state.items.reduce(
      (sum, item) =>
        sum + (item.price ?? 0) * item.quantity,
      0
    );

    return {
      totalItems,
      totalAmount,
    };
  }, [state.items]);

  const addItem = (item) => {
    dispatch({ type: "ADD_ITEM", payload: { item } });
  };

  const updateQuantity = (productId, variantId, optionKey, quantity) => {
    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { productId, variantId, optionKey, quantity },
    });
  };

  const removeItem = (productId, variantId, optionKey) => {
    dispatch({
      type: "REMOVE_ITEM",
      payload: { productId, variantId, optionKey },
    });
  };

  const updateItemDetails = (productId, variantId, optionKey, updates) => {
    dispatch({
      type: "MERGE_ITEM_DETAILS",
      payload: { productId, variantId, optionKey, updates },
    });
  };

  const clearCart = () => {
    dispatch({ type: "CLEAR" });
    clearLocalCartStorage();
  };

  // Synchronisation globale avec le serveur pour les utilisateurs authentifiés
  // (permet au badge du panier dans le header de s'afficher dès le chargement
  // d'une page publique, sans avoir à ouvrir le drawer du panier)
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser({ forceRefresh: false });
  const cachedUser = authService.getCachedUser();
  const isAuthenticated = authService.isAuthenticated();
  const userId = user?._id || user?.id || cachedUser?._id || cachedUser?.id;
  const hasSyncedRef = useRef(false);
  const lastSyncedDataRef = useRef(null);
  const wasAuthenticatedRef = useRef(isAuthenticated);

  // Vider le panier local dès qu'on passe de connecté → déconnecté
  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;

    if (!wasAuthenticated || isAuthenticated) return;

    dispatch({ type: "CLEAR" });
    clearLocalCartStorage();
    hasSyncedRef.current = false;
    lastSyncedDataRef.current = null;
    queryClient.removeQueries({ queryKey: ["cart"] });
  }, [isAuthenticated, queryClient]);

  const { data: cartData, isLoading: isCartLoading } = useCartQuery(userId, {
    enabled: isAuthenticated && !!userId,
  });

  useEffect(() => {
    if (!isAuthenticated || !userId || isCartLoading || !cartData) {
      return;
    }

    const cartDataString = JSON.stringify(cartData);
    if (hasSyncedRef.current && lastSyncedDataRef.current === cartDataString) {
      return;
    }

    const serverCartItems = getCartItemsArrayFromCartApiResponse(cartData);

    if (Array.isArray(serverCartItems)) {
      hasSyncedRef.current = true;
      lastSyncedDataRef.current = cartDataString;

      dispatch({ type: "CLEAR" });

      serverCartItems.forEach((serverItem) => {
        const normalizedItem = normalizeServerItemToCartItem(serverItem);
        if (normalizedItem) {
          dispatch({ type: "ADD_ITEM", payload: { item: normalizedItem } });
        }
      });
    }
  }, [cartData, isAuthenticated, userId, isCartLoading]);

  // Réinitialiser le flag de synchronisation si l'utilisateur change
  useEffect(() => {
    hasSyncedRef.current = false;
    lastSyncedDataRef.current = null;
  }, [userId]);

  const refetchCart = () => {
    hasSyncedRef.current = false;
    lastSyncedDataRef.current = null;
    queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
  };

  const value = useMemo(
    () => ({
      items: state.items,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      updateItemDetails,
      refetchCart,
      ...totals,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.items, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart doit être utilisé dans un CartProvider");
  }
  return context;
};
